import type { CurrencyCode, Price } from '../types/price';

/** Divisor to convert cents (integer) to major currency units. */
export const CENTS_PER_UNIT = 100;

/**
 * Format an amount in cents to a localized currency string.
 *
 * @param cents    - Amount in smallest currency unit (e.g. euro-cents)
 * @param locale   - BCP 47 locale string (defaults to browser locale)
 * @param currency - ISO 4217 currency code (defaults to 'EUR')
 * @param minimumFractionDigits - Minimum decimal places (defaults to 0)
 */
export function formatCurrency(
  cents: number,
  locale?: string,
  currency = 'EUR',
  minimumFractionDigits = 0,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
  }).format(cents / CENTS_PER_UNIT);
}

/**
 * Format a Price object to a localized currency string.
 *
 * @param price  - A Price object with `value` (cents) and `currency` (ISO 4217)
 * @param locale - BCP 47 locale string (defaults to browser locale)
 */
export function formatPrice(price: Price, locale?: string): string {
  return formatCurrency(price.value, locale, price.currency);
}

/**
 * Convert user-entered major-unit input (e.g. euros) to a Price object in cents.
 *
 * @param majorUnits - Amount in major currency units (e.g. 45.99)
 * @param currency   - ISO 4217 currency code (defaults to 'EUR')
 */
export function parsePriceInput(majorUnits: number | string, currency: CurrencyCode = 'EUR'): Price {
  const num = typeof majorUnits === 'string' ? Number.parseFloat(majorUnits) : majorUnits;
  return { value: Math.round(num * CENTS_PER_UNIT), currency };
}

/**
 * Get the currency symbol for a given ISO 4217 currency code.
 *
 * @param currency - ISO 4217 currency code (defaults to 'EUR')
 * @param locale   - BCP 47 locale string (defaults to browser locale)
 */
export function getCurrencySymbol(currency = 'EUR', locale?: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency })
    .formatToParts(0)
    .find((p) => p.type === 'currency')?.value ?? currency;
}
